import { clearAccessToken, getAccessToken, setAccessToken } from './authStorage';

const FALLBACK_API_BASE_URL = '';
const FALLBACK_API_PREFIX = '/api';

const normalizeBaseUrl = (value = '') => value.replace(/\/+$/, '');
const normalizePrefix = (value = '') => {
  if (!value) {
    return '';
  }
  const withLeadingSlash = value.startsWith('/') ? value : `/${value}`;
  return withLeadingSlash.replace(/\/+$/, '');
};

export const apiBaseUrl = normalizeBaseUrl(
  process.env.REACT_APP_API_BASE_URL || FALLBACK_API_BASE_URL
);
export const apiPrefix = normalizePrefix(process.env.REACT_APP_API_PREFIX || FALLBACK_API_PREFIX);

export const buildApiUrl = (path = '') => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const shouldPrefixApi = apiPrefix && !apiBaseUrl.endsWith(apiPrefix);
  const pathWithPrefix = shouldPrefixApi ? `${apiPrefix}${normalizedPath}` : normalizedPath;
  return `${apiBaseUrl}${pathWithPrefix}`;
};

const readErrorMessage = async (response, fallbackMessage) => {
  const data = await response.json().catch(() => ({}));
  return data?.message || fallbackMessage;
};

let refreshPromise = null;
let unauthorizedHandler = null;

const createRequestOptions = (options = {}) => {
  const headers = new Headers(options.headers || {});
  return {
    ...options,
    headers,
    credentials: 'include',
  };
};

const requestAccessTokenRefresh = async () => {
  if (!refreshPromise) {
    refreshPromise = fetch(buildApiUrl('/users/refresh'), {
      method: 'POST',
      credentials: 'include',
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(await readErrorMessage(response, 'Session refresh failed.'));
        }

        const data = await response.json().catch(() => ({}));
        const accessToken = data?.accessToken;

        if (!accessToken) {
          throw new Error('Session refresh failed.');
        }

        setAccessToken(accessToken);
        return accessToken;
      })
      .catch((error) => {
        clearAccessToken();
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

const finalizeResponse = async (response, fallbackMessage, config) => {
  if (!response.ok) {
    const error = new Error(await readErrorMessage(response, fallbackMessage));
    error.status = response.status;
    throw error;
  }

  const data = response.status === 204 ? null : await response.json().catch(() => null);

  if (config.includeStatus) {
    return { data, status: response.status };
  }

  return data;
};

export const refreshAccessToken = requestAccessTokenRefresh;
export const setUnauthorizedHandler = (handler) => {
  unauthorizedHandler = handler;
};

export const apiRequest = async (
  path,
  options = {},
  fallbackMessage = 'Request failed.',
  config = {}
) => {
  const requestOptions = createRequestOptions(options);
  const existingAccessToken = getAccessToken();

  if (!requestOptions.headers.has('Authorization') && existingAccessToken && config.attachAccessToken) {
    requestOptions.headers.set('Authorization', `Bearer ${existingAccessToken}`);
  }

  const isAuthenticatedRequest = requestOptions.headers.has('Authorization');

  let response = await fetch(buildApiUrl(path), requestOptions);

  if (
    response.status === 401 &&
    isAuthenticatedRequest &&
    !config.skipAuthRefresh
  ) {
    try {
      const refreshedAccessToken = await requestAccessTokenRefresh();
      requestOptions.headers.set('Authorization', `Bearer ${refreshedAccessToken}`);
      response = await fetch(buildApiUrl(path), requestOptions);
    } catch {
      await unauthorizedHandler?.();
      const error = new Error('Your session has expired. Please sign in again.');
      error.status = 401;
      throw error;
    }
  }

  if (response.status === 401 && isAuthenticatedRequest) {
    await unauthorizedHandler?.();
  }

  return finalizeResponse(response, fallbackMessage, config);
};
